/**
 * Normalises `GET /get_analytics_data`.
 *
 * The same shape on both Ergo deployments, differing only in how a row
 * identifies itself — `num_plate` for motor, `address` for property, exactly as
 * the case list does.
 *
 * Its job is to turn 188 rows into the few things worth acting on, and to be
 * honest about coverage: `inspection_duration` was recorded for 55 of 188 motor
 * inspections and `trinetra_rating` for 11, so an average over "all" of them
 * would be a fiction. Every derived figure carries how many rows it came from.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

const numberOr = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Counts by key, highest first, then alphabetically so ties are stable. */
const rank = (counts) =>
  [...counts.entries()]
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));

const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** "2026-09-01 13:20:44" -> "2026-09-01" */
export const dayOf = (value) =>
  typeof value === "string" && value.length >= 10 ? value.slice(0, 10) : null;

/**
 * Seconds as something readable. Durations here run from ~2 minutes to
 * hours, so a fixed unit would be wrong at one end or the other.
 */
export const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 90) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${minutes.toFixed(minutes < 10 ? 1 : 0)}m`;
  return `${(minutes / 60).toFixed(1)}h`;
};

/**
 * A backend enum as a person would write it.
 *
 * The API answers with its own identifiers — FRONT_BUMPER, RIGHT_QUARTER_PANEL,
 * SCRATCH — which are correct as keys and shout on a page. The raw value stays
 * the key everywhere it identifies something (filter values, React keys); this
 * is only for display.
 */
export const prettyEnum = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const words = value.trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  // Only recase a value that is shouting. A name the backend already wrote in
  // mixed case was written that way on purpose.
  if (words !== words.toUpperCase()) return words;
  const lower = words.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/** First letter up, nothing else touched — the API's messages start both ways. */
export const sentenceCase = (value) => {
  if (typeof value !== "string" || !value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * A warning message a reader can group by.
 *
 * The API's message sometimes carries the specifics of one row after a colon —
 * "Incorrect side captured : frontRight", "Image captured is not a passenger
 * car: frontLeft, rearLeft, rearRight" — so using it whole as the label for a
 * whole group of rows reads as if every one of them was about frontRight. The
 * part before the colon is the warning; the part after belongs to the row, and
 * the row detail still shows it in full.
 */
export const warningLabel = (message) => {
  if (typeof message !== "string" || !message.trim()) return "";
  return message.split(":")[0].trim().replace(/\s+/g, " ");
};

/**
 * Labels for a set of warnings, with codes no longer shown.
 *
 * Trimming at the colon can make two different codes read identically. Nothing
 * in the sample data does, but the codes are the backend's and can change, so
 * where a trimmed label would collide the full message is kept instead — a
 * duplicated row in a chart is worse than a long one, because it looks like a
 * bug and hides that two distinct things were counted.
 */
export const labelWarnings = (warnings) => {
  const list = asArray(warnings);
  const short = new Map();
  list.forEach((warning) => {
    const label = warningLabel(warning?.message) || warning?.code || "";
    short.set(label, (short.get(label) || 0) + 1);
  });
  return list.map((warning) => {
    const label = warningLabel(warning?.message) || warning?.code || "";
    return {
      ...warning,
      label: sentenceCase(short.get(label) > 1 ? warning.message || label : label),
    };
  });
};

/**
 * Every day between the first and the last, including the quiet ones.
 *
 * Counting only the days that had rows draws a chart with no gaps in it: motor
 * really jumps 2026-08-26 to 2026-08-31, and five bars side by side say the
 * period was busy throughout when it was not. A zero-height day is the truth
 * and it is also what makes the shape of the period readable.
 *
 * Capped: past a few months of span the gaps stop being informative and start
 * being thousands of DOM nodes, so a long period keeps only its active days.
 */
const MAX_FILLED_DAYS = 180;

export const fillDays = (counts) => {
  const days = [...counts.keys()].sort();
  if (days.length < 2) {
    return days.map((date) => ({ date, count: counts.get(date) }));
  }
  const first = Date.parse(`${days[0]}T00:00:00Z`);
  const last = Date.parse(`${days[days.length - 1]}T00:00:00Z`);
  if (!Number.isFinite(first) || !Number.isFinite(last)) {
    return days.map((date) => ({ date, count: counts.get(date) }));
  }
  const span = Math.round((last - first) / 86400000) + 1;
  if (span > MAX_FILLED_DAYS) {
    return days.map((date) => ({ date, count: counts.get(date) }));
  }
  return Array.from({ length: span }, (unused, index) => {
    const date = new Date(first + index * 86400000).toISOString().slice(0, 10);
    return { date, count: counts.get(date) || 0 };
  });
};

export const toAnalytics = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const rows = asArray(raw.data);

  const failureReasons = Object.entries(raw.failed_reason_breakdown || {})
    .map(([reason, count]) => ({ reason, count: numberOr(count) }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));

  // Warnings are per row and repeat across them; the message is what a reader
  // needs, the code is what they quote in a ticket.
  const warningCounts = new Map();
  const warningMessages = new Map();
  rows.forEach((row) =>
    asArray(row?.warnings).forEach((warning) => {
      const code = warning?.warning_code;
      if (!code) return;
      warningCounts.set(code, (warningCounts.get(code) || 0) + 1);
      if (warning.warning_message && !warningMessages.has(code)) {
        warningMessages.set(code, warning.warning_message);
      }
    }),
  );

  // Motor only — a property inspection reports no panels.
  const panelCounts = new Map();
  const damageTypeCounts = new Map();
  rows.forEach((row) =>
    asArray(row?.damaged_parts).forEach((part) => {
      if (part?.partName) {
        panelCounts.set(part.partName, (panelCounts.get(part.partName) || 0) + 1);
      }
      asArray(part?.damType).forEach((type) => {
        if (type) damageTypeCounts.set(type, (damageTypeCounts.get(type) || 0) + 1);
      });
    }),
  );

  const dayCounts = new Map();
  rows.forEach((row) => {
    const day = dayOf(row?.created_at);
    if (day) dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
  });

  const durations = rows
    .map((row) => row?.inspection_duration)
    .filter((value) => Number.isFinite(value) && value > 0);

  const ratings = rows
    .map((row) => row?.trinetra_rating)
    .filter((value) => Number.isFinite(value) && value > 0);

  const links = raw.trinetra_links_summary || {};
  const generated = numberOr(links.total_links_generated);
  const completed = numberOr(links.completed);

  const total = numberOr(raw.total_count, rows.length);
  const passed = numberOr(raw.passed_count);
  const failed = numberOr(raw.failed_count);

  return {
    // How much of the period the page actually received. next_page is -1 when
    // there is no more, so a page limit smaller than the total would mean these
    // per-row derivations cover only part of it — worth saying rather than
    // presenting as the whole.
    coverage: {
      rows: rows.length,
      total,
      complete: numberOr(raw.next_page, -1) === -1 && rows.length >= total,
    },

    totals: {
      inspections: total,
      passed,
      failed,
      // Out of the two counts, not out of `total`: they are what the backend
      // actually classified.
      passRate: passed + failed > 0 ? passed / (passed + failed) : null,
    },

    /** Every day from the first to the last, oldest first — gaps included. */
    daily: fillDays(dayCounts),

    /** How many of those days actually had inspections. */
    daysWithActivity: dayCounts.size,

    failureReasons,

    warnings: labelWarnings(
      rank(warningCounts).map(({ key, count }) => ({
        code: key,
        message: warningMessages.get(key) || key,
        count,
      })),
    ),

    // `name`/`type` stay the raw value — they are the filter's key — with the
    // readable form alongside.
    panels: rank(panelCounts).map(({ key, count }) => ({
      name: key,
      label: prettyEnum(key),
      count,
    })),
    damageTypes: rank(damageTypeCounts).map(({ key, count }) => ({
      type: key,
      label: prettyEnum(key),
      count,
    })),

    duration: {
      median: median(durations),
      mean: durations.length
        ? durations.reduce((sum, value) => sum + value, 0) / durations.length
        : null,
      recorded: durations.length,
      of: rows.length,
    },

    rating: {
      mean: ratings.length
        ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length
        : null,
      recorded: ratings.length,
      of: rows.length,
      // Always all five steps, including the empty ones: a distribution with
      // gaps closed up reads as if nobody could have given that score.
      distribution: [5, 4, 3, 2, 1].map((score) => ({
        score,
        count: ratings.filter((value) => Math.round(value) === score).length,
      })),
      lowest: ratings.length ? Math.min(...ratings) : null,
    },

    /**
     * The capture-link funnel.
     *
     * Worth its own block because the live figures are stark: 3 of 5 motor
     * links and 15 of 17 property links expired without being used. That is a
     * fact about how the links are reaching people, not about the inspections.
     */
    links: {
      generated,
      completed,
      startedNotCompleted: numberOr(links.started_not_completed),
      notStarted: numberOr(links.not_started),
      expired: numberOr(links.expired),
      completionRate: generated > 0 ? completed / generated : null,
    },
  };
};

export default toAnalytics;
